import React, { useState, useEffect, useMemo, useContext } from "react";
import axios from "axios";
import { UserContext } from "../../contexts";
import { ProductSearchHelper } from "../../helpers";
import { useMarketplaceAccounts } from "../../hooks/marketplaceAccounts";
import PriceRange from "./PriceRange";
import { Button, Checkbox, Chip, Icon, IconButton } from "../../components/ui";
import "./rc-filters.scss";

// STORE filter is positioned first by request. Its options come from
// /api/stores/{id}/marketplace-accounts (all rows, active + inactive)
// plus two flat-column pseudo-options for eBay / Watchfinder.
const ALL_FILTERS = [
  { value: "store",         label: "Store" },
  { value: "status",        label: "Status" },
  // listings.internal_product. Server-fed like Condition / Case Material, so the
  // options arrive with counts; the four ids are the enum values.
  { value: "internal_product", label: "Owned By" },
  { value: "brand_id",      label: "Brand" },
  { value: "model_id",      label: "Model" },
  { value: "price",         label: "Price" },
  { value: "condition",     label: "Condition" },
  { value: "location",      label: "Location" },
  { value: "case_material", label: "Case Material" },
  { value: "case_diameter", label: "Case Size" },
  { value: "dial_color",    label: "Dial Color" },
];

// Option-id encoding for the STORE filter (string ids — survive the
// shop↔manage converters in ListingSearch.jsx without ambiguity):
//   "acc:<id>"  → store_marketplace_accounts row
//   "ebay"      → listings.is_ebay
//   "wf"        → listings.is_wf
const STORE_OPT_EBAY = { id: "ebay", name: "eBay" };
const STORE_OPT_WF   = { id: "wf",   name: "Watchfinder" };

const RCFilters = ({
  active,
  close,
  search: defaultSearch,
  onApply,
  includeStatusFilter = false,
  // Store is an admin / marketplace facet — off for the public shop search.
  includeStoreFilter = true,
  // Owned By is an internal ownership facet (who the store holds the watch
  // for), meaningless to a shopper — off for the public shop search.
  includeOwnedByFilter = false,
}) => {
  const filters = ALL_FILTERS.filter((f) => {
    if (f.value === "status") return includeStatusFilter;
    if (f.value === "store") return includeStoreFilter;
    if (f.value === "internal_product") return includeOwnedByFilter;
    return true;
  });

  const initialSearch = {
    ...defaultSearch,
    filters: {
      ...defaultSearch.filters,
      category_id:
        (defaultSearch.filters && defaultSearch.filters.category_id) || 9,
    },
  };

  const [search, setSearch]             = useState(initialSearch);
  const [activeFilter, setActiveFilter] = useState(filters[0].value);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading]           = useState(false);

  // STORE filter options come from the marketplace-accounts API (active +
  // inactive). Fetched once via react-query so this is cheap. Uses the
  // ACTIVE store (Phase 5 store-switcher), not the user's home store — a
  // multi-store rep filtering search results while switched into a
  // different store must see THAT store's accounts, not their home store's.
  const { user, activeStoreId } = useContext(UserContext) || {};
  const { accounts: marketplaceAccounts } = useMarketplaceAccounts(activeStoreId ?? user?.store_id_m);
  const storeFilterOptions = useMemo(() => {
    const chronoOpts = marketplaceAccounts
      .filter((a) => a.marketplace === "chrono24")
      .sort((a, b) => {
        if (a.slug === null && b.slug !== null) return -1;
        if (b.slug === null && a.slug !== null) return 1;
        return (a.label || "").localeCompare(b.label || "");
      })
      .map((a) => ({
        id: `acc:${a.id}`,
        name: a.is_enabled ? a.label : `${a.label} (inactive)`,
      }));
    return [...chronoOpts, STORE_OPT_EBAY, STORE_OPT_WF];
  }, [marketplaceAccounts]);

  useEffect(() => {
    if (!activeFilter) return;

    // STORE filter has client-side options — no server call needed.
    if (activeFilter === "store") {
      setFilterOptions(storeFilterOptions);
      return;
    }

    setFilterOptions(null);

    const load = async () => {
      setLoading(true);
      try {
        const prepared = ProductSearchHelper.prepareForServer(search);
        const res = await axios.post("/api/search/filter-values", {
          ...prepared,
          filter: activeFilter,
        });
        const data = res.data;

        if (activeFilter === "price") {
          const pd = data && data[0] ? data[0] : null;
          setFilterOptions([{ min: pd?.min ?? 0, max: pd?.max ?? 1000000 }]);
        } else {
          setFilterOptions(Array.isArray(data) ? data : []);
        }
      } catch {
        setFilterOptions(activeFilter === "price" ? [{ min: 0, max: 1000000 }] : []);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [activeFilter, search.filters?.category_id, search.reset, storeFilterOptions]);

  const selections = search.filters[activeFilter] || [];

  const onToggleOption = (option) => {
    const current = search.filters[activeFilter] || [];
    const exists  = current.some((s) => s.id === option.id);
    const next    = exists
      ? current.filter((s) => s.id !== option.id)
      : [...current, option];
    setSearch((prev) => ({
      ...prev,
      filters: { ...prev.filters, [activeFilter]: next },
    }));
  };

  const onRangeChange = (values) => {
    const str = `range:${values[0]}-${values[1]}`;
    setSearch((prev) => ({
      ...prev,
      filters: { ...prev.filters, [activeFilter]: [{ id: str, name: str }] },
    }));
  };

  const onReset = () => {
    setSearch((prev) => ({
      ...prev,
      query: "",
      filters: { category_id: prev.filters.category_id },
      reset: (prev.reset || 0) + 1,
    }));
  };

  const activeCount = Object.entries(search.filters).filter(
    ([k, v]) => k !== "category_id" && v && (Array.isArray(v) ? v.length > 0 : true)
  ).length;

  // Prepare options
  let options       = [];
  let popularOptions = [];
  let priceRange    = null;

  if (filterOptions) {
    if (activeFilter === "price") {
      priceRange = filterOptions[0];
    } else {
      const valid  = filterOptions.filter((o) => o && o.id != null);
      const sorted = [...valid].sort((a, b) =>
        a.name < b.name ? -1 : a.name > b.name ? 1 : 0
      );
      popularOptions = valid.slice(0, 8);
      options        = sorted;
    }
  }

  if (!active) return null;

  return (
    <div className="rc-filter-overlay">
      <div className="rc-filter-overlay__backdrop" onClick={close} />

      <div className="rc-filter-drawer">
        {/* Header */}
        <div className="rc-filter-drawer__header">
          <span className="rc-filter-drawer__title">Filter</span>
          {activeCount > 0 && (
            <span className="rc-filter-drawer__active-count">{activeCount} active</span>
          )}
          <IconButton variant="ghost" size="sm" label="Close" data-btn="filters-close" onClick={close}>
            <Icon name="X" size="sm" />
          </IconButton>
        </div>

        {/* Body */}
        <div className="rc-filter-drawer__body">

          {/* Left nav */}
          <nav className="rc-filter-drawer__nav">
            {filters.map((f) => {
              const hasValue =
                search.filters[f.value] &&
                Array.isArray(search.filters[f.value]) &&
                search.filters[f.value].length > 0;
              return (
                <Button
                  key={f.value}
                  variant="ghost"
                  size="sm"
                  active={f.value === activeFilter}
                  aria-pressed={f.value === activeFilter}
                  data-btn="filters-open"
                  onClick={() => setActiveFilter(f.value)}
                  trailingIcon={hasValue ? <span className="rc-filter-drawer__nav-dot" /> : null}
                >
                  {f.label}
                </Button>
              );
            })}
          </nav>

          {/* Right content */}
          <div className="rc-filter-drawer__content">
            {loading ? (
              <div className="rc-filter-drawer__loading">
                <div className="rc-filter-drawer__spinner" />
              </div>
            ) : (
              <>
                {/* Price slider */}
                {priceRange && (
                  <div className="rc-filter-drawer__section rc-filter-drawer__section--price">
                    <p className="rc-filter-drawer__section-title">Price Range</p>
                    <PriceRange {...priceRange} onChange={onRangeChange} />
                  </div>
                )}

                {/* Popular chips */}
                {popularOptions.length > 0 && (
                  <div className="rc-filter-drawer__section">
                    <p className="rc-filter-drawer__section-title">Popular</p>
                    <div className="rc-filter-drawer__chips">
                      {popularOptions.map((o) => {
                        const sel = selections.some((s) => s.id === o.id);
                        return (
                          <Chip
                            key={o.id}
                            selected={sel}
                            onClick={() => onToggleOption(o)}
                          >
                            {o.name}
                          </Chip>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Full options list */}
                {options.length > 0 && (
                  <div className="rc-filter-drawer__section">
                    <p className="rc-filter-drawer__section-title">
                      {popularOptions.length > 0 ? "All" : activeFilter === "status" ? "Status" : "Options"}
                    </p>
                    <div className="rc-filter-drawer__options">
                      {options.map((o) => {
                        const sel = selections.some((s) => s.id === o.id);
                        return (
                          <div key={o.id} className="rc-filter-drawer__option">
                            <Checkbox
                              checked={sel}
                              onChange={() => onToggleOption(o)}
                              label={o.name}
                            />
                            {o.count != null && (
                              <span className="rc-filter-drawer__option-count">{o.count}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!priceRange && options.length === 0 && popularOptions.length === 0 && (
                  <p className="rc-filter-drawer__empty">No options available</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="rc-filter-drawer__footer">
          <Button variant="ghost" data-btn="filters-cancel" onClick={close}>Cancel</Button>
          <div className="rc-filter-drawer__footer-right">
            <Button variant="secondary" data-btn="filters-reset" onClick={onReset}>Reset</Button>
            <Button variant="primary" data-btn="filters-apply" onClick={() => onApply(search)}>
              Apply{activeCount > 0 ? ` (${activeCount})` : ""}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCFilters;
